import {currency} from '@/support/String';
import {Component, Vue, Watch} from 'vue-property-decorator';
import Sidepanel from '@/views/Webshop/Sidepanel/Sidepanel';
import {Product} from '@/models/Product';
import {Contact as ContactModel} from '@/models/Contact';
import {Order} from '@/models/Order';
import {OrderItem} from '@/models/OrderItem';
import {AxiosError} from 'axios';
import debounce from 'debounce';
import ErrorHandler from '@/support/ErrorHandler';
import {cloneDeep} from 'lodash';
import LoaderCard from '@/components/loader-card/LoaderCard';
import {Options} from '@/components/mi-dialog/MiDialog';
import {ProductVariantFilterItem} from "@/components/CustomFilters/VariantFilters/VariantFilters";
import {ProductVariant, ProductVariantIncludes} from '@/models/ProductVariant';
import {FastProductVariant} from "@/models/FastProductVariant";
import UploadOrderItemsCsvDialog from "@/components/CustomDialog/UploadOrderItemsCsvDialog/UploadOrderItemsCsvDialog";
import {RoleEnum} from "@/models/User";
import { BoxRequest } from '@/models/BoxRequest';
// CLD-13: Trengo is disabled in public/index.html, so there is no third-party
// launcher left to hook — Maia renders her own bubble in that spot instead.
// Restore this together with the Trengo block to go back to hooking their icon.
// import TrengoLauncher, { TrengoHookResult } from '@/support/TrengoLauncher';

@Component<Webshop>({
  components: {
    Sidepanel,
    LoaderCard,
    UploadOrderItemsCsvDialog
  },
  filters: {
    currency: (value: string) => {
      return value ? currency(parseFloat(value)) : currency(0);
    }
  }
})
export default class Webshop extends Vue {
  public $pageTitle = 'Webshop';

  protected contact: ContactModel = new ContactModel();

  protected contactAnswers: any[] = [];

  protected contactImages: any[] = [];

  protected productDetailDialog: boolean = false;

  /** Open state of the Maia styling coach panel (CLD-13). */
  // CLD-13: while Trengo is disabled Maia always renders her own bubble, so this
  // flag has nothing left to decide. Restore it alongside the Trengo hook.
  // protected isShowingMaiaLauncher: boolean = true;

  protected imageDialog: boolean = false;

  protected products: Product[] = [];

  protected orderItems: any[] = [];

  protected currentDetail = {};

  protected totalPages: number = 1;

  protected page: number = 1;

  protected totalProducts: number = 0;

  protected currentOrder: Order | null = null;

  protected orderStatus: string = '';

  protected selectedOrderStatusUpdate: string = '';

  protected orderUserId: string = '';

  private shopCategories: string[] = [];

  protected selectedApprovedStatus: boolean = false;

  protected isLoading: boolean = true;

  protected isLoadingProducts: boolean = false;

  protected productComment = '';

  protected productSearch = '';

  protected isShowingImportOrderDialog = false;

  public searchDebounce: Function = this.handleSearch();

  protected isShowingFilters = false;

  protected latestBoxRequest: BoxRequest | null = null;

  protected filtersHeight = 0;

  protected approvedOptions: approvedOption[] = [{
    label: 'Ok',
    value: true,
  }, {
    label: 'Niet ok',
    value: false,
  }]

  protected currentIndex: number = 0;
  protected images: any = [
    {
      image_url: 'https://picsum.photos/250/300/?random',
    },
    {
      image_url: 'https://placeimg.com/250/300/any',
    },
    {
      image_url: 'https://picsum.photos/250/300/?random',
    },
    {
      image_url: 'https://placeimg.com/250/300/any',
    }
  ]

  public commentDebounce: Function = this.handleComment();
  protected searchInput: string = '';

  // Note, also change the defaults in getProductFilters() ...
  protected productFilter: ProductVariantFilterItem = {
    out_of_stock: "0",
    no_images: "0",
    withoutAttributes: "0",
    status: ['active'],
    product:'',
    seasons: [],
    block_date_from: '',
    block_date_until: '',
    final_return_date_from: '',
    final_return_date_until: '',
    attributes: [],
    order: '',
    tags: [],
  };

  protected optionFilter: OptionFilter = {

  };

  protected webshopProductVariantIncludes = [
    ProductVariantIncludes.IMAGES,
    ProductVariantIncludes.AVAILABLE_SIZES,
    ProductVariantIncludes.COLOR,
    ProductVariantIncludes.BRAND,
    ProductVariantIncludes.TAGS,
    ProductVariantIncludes.PREVIOUSLY_ORDERED_PRODUCT,
  ];

  public mounted(): void {
    this.initialize();
    // this.hookMaiaToChatIcon();
  }

  // CLD-13: pointed the existing (Trengo) chat icon at Maia. Trengo is disabled
  // in public/index.html for now, so there is nothing to hook and Maia opens from
  // her own bubble. Restore both together.
  //
  // Note if you do: isShowingMaiaChat now lives in App, not here — the widget was
  // moved above the router-view so a product opening in the main window does not
  // unmount the conversation.
  //
  // /**
  //  * Points the existing chat icon in the shop at Maia (briefing 5.2). That icon
  //  * belongs to Trengo, so the hook is best-effort: when it cannot be confirmed
  //  * Maia keeps her own bubble as a guaranteed entry point.
  //  */
  // protected async hookMaiaToChatIcon(): Promise<void> {
  //   const result = await TrengoLauncher.activate(() => {
  //     this.isShowingMaiaChat = true;
  //   });
  //
  //   this.isShowingMaiaLauncher = result !== TrengoHookResult.Hooked;
  // }
  //
  // /**
  //  * Open the panel next to whatever opened it: bottom-left with the Trengo chat
  //  * icon, bottom-right when Maia falls back to her own bubble.
  //  */
  // protected get maiaChatAnchor(): MaiaAnchor {
  //   return this.isShowingMaiaLauncher ? 'right' : 'left';
  // }

  protected async initialize(): Promise<void> {
    this.isLoading = true;
    // On loading, load the previous product page, so on returning to the webshop, it remembers the page
    this.page = this.getPage();
    await this.setFilters();
    await this.listOrderItems();
    await this.getOrder();
    await this.listProducts();
    this.isLoading = false;
  }

  public destroyed() {
    // TrengoLauncher.deactivate();
    this.$store.dispatch('updateProductFilters', this.productFilter);
    this.setPage(this.page);
  }

  public handleSearch() {
    return debounce((searchInput: string) => {
      // Reset to page 1 on searching
      this.page = 1;
      this.listProducts();
    }, 400);
  }

  protected isTopSeller(product: Record<string, any>): boolean {
    return product.tags.some((tag: any) => tag.uuid === '9c40f821-4a15-4a9a-9a52-ce325fb4f850');
  }

  protected isAOVBooster(product: Record<string, any>): boolean {
    return product.tags.some((tag: any) => tag.uuid === '9c40f838-ce80-4f61-8d5a-4fee1d0ca94d');
  }

  protected isRisingStar(product: Record<string, any>): boolean {
    return product.tags.some((tag: any) => tag.uuid === '9c40f82e-8c26-494c-8b2c-b00978801c1d');
  }

  protected setPage(page: number) {
    localStorage.setItem(`${this.$route.name}_page`, page.toString());
  }

  protected getPage() {
    const result = localStorage.getItem(`${this.$route.name}_page`);

    if (result) {
      return Number(result);
    }

    return 1;
  }

  protected showFilters(): void {
    if (! this.isShowingFilters) {
      this.isShowingFilters = true;
      const innerFilters = document.getElementsByClassName('inner-filters');
      this.filtersHeight = innerFilters[0].clientHeight;
    } else {
      this.isShowingFilters = false;
      this.filtersHeight = 0;
    }
  }

  protected async setFilters(): Promise<void> {
    const storedSessionFilters = await this.getProductFilters();

    if (!storedSessionFilters) {
      return;
    }

    // Set the default values
    if (!("out_of_stock" in storedSessionFilters)) {
      storedSessionFilters.out_of_stock = this.productFilter.out_of_stock;
    }

    if (!("no_images" in storedSessionFilters)) {
      storedSessionFilters.no_images = this.productFilter.no_images;
    }

    if (!("withoutAttributes" in storedSessionFilters)) {
      storedSessionFilters.withoutAttributes = this.productFilter.withoutAttributes;
    }

    this.productFilter = storedSessionFilters as ProductVariantFilterItem;
  }

  public getProductFilters() {
    const filters = localStorage.getItem(`${this.$route.name}_productFilters`);

    if (filters) {
      var parsedobj = JSON.parse(filters);

      if (parsedobj) {
        parsedobj = {
          ...parsedobj,
          status: typeof parsedobj.status === 'string' ? [parsedobj.status] : parsedobj.status
        };
      }

      return parsedobj;
    }

    return this.productFilter;
  }


  protected handleFilterChanged(filter: ProductVariantFilterItem) {
    this.productFilter = filter;
    // Reset the page to 1 if a filter changed, but not on loading, as we then want to get a different page
    if (! this.isLoading) {
      this.page = 1;
    }
    this.listProducts();
  }

  protected checkDuplicateOrderItems(productId: string) {
    let duplicate = false;
    this.orderItems.forEach((item) => {
      if (productId === item.product.id) {
        duplicate = true;
      }
    })
    return duplicate;
  }

  protected createOrderItem(productId: string) {
    const orderItemPayload = {
      product: productId,
    }
    new OrderItem()
    .order(this.$route.params.id)
    .create(orderItemPayload)
    .then((response: any) => {
      this.listOrderItems();
      if (response) {
        this.productDetailDialog = false;
      }
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
    this.listOrderItems();
  }

  protected filterChange() {
    this.page = 1;
    this.setPage(this.page);
    this.listProducts();
  }

  protected resetAllFilters() {
    const defaultFilters: ProductVariantFilterItem = {
      out_of_stock: "0",
      no_images: "0",
      withoutAttributes: "0",
      status: ['active'],
      product:'',
      seasons: [],
      block_date_from: '',
      block_date_until: '',
      final_return_date_from: '',
      final_return_date_until: '',
      attributes: [],
    };
    this.productFilter = cloneDeep(defaultFilters);

    this.filterChange();
  }


  protected async listOrderItems(): Promise<void> {
    await new OrderItem()
    .order(this.$route.params.id)
    .include('product')
    .limit(100)
    .all()
    .then((response: any) => {
      this.orderItems = response;
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected getFilters()
  {
    return {
      ...this.productFilter,
      ...{
        status: 'active',
        search: this.productSearch,
        out_of_stock: "0",
        is_webshop: "1",
        order: this.$route.params.id,
      }};
  }

  protected async listProducts(): Promise<void> {
    this.isLoadingProducts = true;

    await new FastProductVariant()
    .page(this.page)
    .filter(this.getFilters())
    .include(this.webshopProductVariantIncludes)
    .limit(18)
    .sort('webshop')
    .all()
    .then((response: any) => {
      this.products = response;
      this.isLoadingProducts = false;

      if (response.length === 0) {
        this.totalProducts = 0;
      } else {
        this.totalProducts = response[0].meta.total;
        this.totalPages = response[0].meta.last_page;
      }
    })
    .catch((error: AxiosError) => {
      this.isLoadingProducts = false;
    });
  }

  public handleComment() {
    return debounce((searchInput: string) => {
      this.saveProductInfo((this.currentDetail as any).id);
    }, 400);
  }

  protected openImageDialog(product: object) {
    this.imageDialog = true;
  }

  protected closeDialog() {
    if (this.productDetailDialog) {
      this.productDetailDialog = false;
      this.currentIndex = 0;
      this.currentDetail = {};
    }
  }

  protected closeImageDialog() {
    if (this.imageDialog) {
      this.imageDialog = false;
    }
  }

  protected changeImageIndex(index: number) {
    this.currentIndex = index;
  }

  protected previousOrNextImage(imageArrayLength: number, add: boolean) {
    let newIndex = 0;
    if (add) {
      newIndex = this.currentIndex + 1;
      if (newIndex >= imageArrayLength) {
        this.currentIndex = 0;
        return;
      }
    } else {
      newIndex = this.currentIndex - 1;
      if (newIndex === -1) {
        this.currentIndex = imageArrayLength - 1;
        return;
      }
    }
    this.currentIndex = newIndex;
  }

  protected async getOrder(): Promise<void> {
    await new Order()
    .include('user')
    .find(this.$route.params.id)
    .then(async (order: Order) => {
      if (!order) {
        return;
      }

      this.currentOrder = order;
      this.orderStatus = order.status as string;

      if (order.status !== 'active' && order.status !== 'inactive' && order.status !== 'preview') {
        this.$router.push(`/webshop/order/${order.id}/order-detail`);
      }

      this.orderUserId = order.user ? order.user.id as string : '';
      await this.getContact();

      // For a warehouse user, we don't need to get the box request
      const currentAuth = this.$store.state.Auth;
      if (currentAuth.roles.some((role: Record<string, any>) => role.id !== RoleEnum.WAREHOUSE)) {
        await this.getBoxRequest();
      }
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    })
  }

  protected async getBoxRequest(): Promise<void> {
    if (!this.orderUserId) {
      return;
    }

    console.log('this.orderUserId: ', this.orderUserId);
    try {
      const requestBoxes = await new BoxRequest().filter({user: this.orderUserId}).sort('created_at', 'DESC').include(['info']).all();
      console.log('requestBoxes: ', requestBoxes);
      const styledTypeBoxRequests = requestBoxes.filter((requestBox: BoxRequest) => requestBox.type === 'styled_box');
      if (styledTypeBoxRequests.length) {
        console.log('styledTypeBoxRequests: ', styledTypeBoxRequests);
        this.latestBoxRequest = styledTypeBoxRequests[0];
      }
    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  protected async getContact(): Promise<void> {
    await new ContactModel()
    .include(['profile', 'answers'])
    .find(this.orderUserId)
    .then((contact: ContactModel) => {
      this.contact = contact;
      // Maia lives in App, above the router-view, so it cannot read this view's
      // contact directly. Publishing the name is all it needs — the backend
      // resolves the customer from the order itself.
      this.$store.commit('MAIA_CUSTOMER_UPDATE', contact && contact.name ? contact.name : '');
      this.$root.$emit('breadcrumbUpdated',
      {
        crumb: [
          { name: 'Orders', path: `/orders` },
          { name: `${this.contact.name}` },
        ],
      }
      );

      if(!this.contact.profile || !this.contact.profile.answers) {
        return;
      }

      this.contact.profile.answers = JSON.parse((this.contact.profile as any).answers);

      this.contact.answers = [...(this.contact.profile as any).answers];
      this.contactAnswers = [...(this.contact.profile as any).answers];
      this.$store.dispatch('updateLeadAnswers', this.contact);
      // this.fetchFiles();
    });
  }

  saveProductInfo(productId: string) {
    const payload = {
      comment: this.productComment,
      is_approved: this.selectedApprovedStatus,
    }
    new ProductVariant({id: productId})
    .update(payload)
    .then((response: any) => {
      this.listProducts();
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected onOrderCsvUploaded(): void {
    this.initialize();
  }

  protected changePage() {
    this.isLoadingProducts = true;
    new FastProductVariant()
      .page(this.page)
      .filter(this.getFilters())
      .include(this.webshopProductVariantIncludes)
      .limit(18)
      .sort('goods-in-summary')
      .all()
      .then((response: any) => {
        this.products = response;
        this.isLoadingProducts = false;
      })
      .catch((error: AxiosError) => {
        this.isLoadingProducts = false;
      });
  }

  protected get dialogOptions(): Options {
    return {
      title: 'Order update',
      text: `Order is updated naar ${this.selectedOrderStatusUpdate}`,
      type: 'success',
      buttons: {
        confirm: {
          text: 'Oke',
          action: () => {
            window.location.href = '/orders';
          }
        },
      },
    };
  }

  protected get canImport() {
    if (this.$store && this.$store.state && this.$store.state.Auth) {
      return !!this.$store.state.Auth.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR]);
    }

    return false;
  }


  @Watch("page")
  public pageChange(value: any, oldValue: any) {
    this.setPage(value);
  }
}

interface approvedOption {
  label: string;
  value: boolean;
}

interface OptionFilter {
  Size?: string;
  Brand?: string;
  Colour?: string;
}

interface FilterColor {
  id?: string;
  name?: string;
  type?: string;
  reference?: string;
}
